import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BOAT_EXCURSIONS } from "../data/site";
import { useLang, loc } from "../data/i18n";
import { BrandSymbol } from "./BrandLogo";

/**
 * Single flagship catalog card — "Sorties bateau" / "Boat trips".
 * All 3 excursions are accessed through the dedicated /excursion-* routes,
 * which are indexable and switch via tabs on those pages.
 */
export default function Experiences() {
    const { t, lang, prefix } = useLang();
    const flagship = loc(
        BOAT_EXCURSIONS.find((e) => e.id === "5iles"),
        lang,
    );

    return (
        <section
            id="experiences"
            className="relative py-24 md:py-36 bg-cream overflow-hidden"
            data-testid="experiences-section"
        >
            <BrandSymbol
                size={200}
                tint="ocean"
                className="absolute top-16 -right-16 opacity-20"
            />
            <BrandSymbol
                size={280}
                className="absolute bottom-20 -left-24 opacity-15"
            />

            <div className="max-w-[1400px] mx-auto px-5 md:px-10 relative">
                {/* Section header */}
                <div className="max-w-3xl mb-14 md:mb-20">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-xs uppercase tracking-[0.3em] text-coral font-bold mb-4"
                    >
                        {t.experiences.kicker}
                    </motion.p>
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="font-serif text-4xl md:text-6xl text-ocean leading-[1.05]"
                    >
                        {t.experiences.titleA}
                        <em className="text-coral not-italic font-serif italic">
                            {t.experiences.titleB}
                        </em>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="mt-6 text-base md:text-lg text-ink/75 leading-relaxed"
                    >
                        {t.experiences.intro}
                    </motion.p>
                </div>

                {/* Single flagship card — full width */}
                <motion.article
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.9 }}
                    className="relative overflow-hidden rounded-2xl bg-white shadow-[0_20px_60px_rgba(27,108,168,0.15)]"
                    data-testid="boat-catalog-card"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-12">
                        {/* Image side */}
                        <div className="relative lg:col-span-7 aspect-[16/10] lg:aspect-auto lg:min-h-[540px] overflow-hidden">
                            <img
                                src={flagship.image}
                                alt={flagship.imageAlt}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-tr from-ocean-900/70 via-ocean-900/25 to-transparent" />
                            <span className="absolute top-6 left-6 text-[10px] uppercase tracking-[0.32em] text-ocean bg-lagoon px-3 py-1.5 font-bold">
                                {flagship.badge}
                            </span>
                        </div>

                        {/* Content side */}
                        <div className="lg:col-span-5 p-8 md:p-12 lg:p-14 flex flex-col justify-between">
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.32em] text-coral font-bold mb-4">
                                    {t.experiences.cardKicker}
                                </p>
                                <h3 className="font-serif text-4xl md:text-5xl text-ocean leading-[1.05] mb-5">
                                    {t.experiences.cardTitle}
                                </h3>
                                <p className="text-base md:text-lg text-ink/80 leading-relaxed mb-8">
                                    {t.experiences.cardText}
                                </p>

                                <div className="border-t border-ocean/15 pt-6 mb-8">
                                    <p className="text-[11px] uppercase tracking-[0.3em] text-lagoon-700 font-bold mb-2">
                                        {t.experiences.flagLabel}
                                    </p>
                                    <p
                                        className="text-ocean font-semibold text-base md:text-lg leading-relaxed"
                                        data-testid="flagship-price-line"
                                    >
                                        {t.experiences.flagLine}
                                    </p>
                                </div>
                            </div>

                            <Link
                                to={`${prefix}/${flagship.slug}`}
                                className="btn-coral group inline-flex items-center gap-3 w-full justify-center"
                                data-testid="view-excursions-cta"
                            >
                                {t.experiences.cta}
                                <span className="transition-transform group-hover:translate-x-1">
                                    →
                                </span>
                            </Link>
                        </div>
                    </div>
                </motion.article>
            </div>
        </section>
    );
}
